import { ExternalApiPayload, ExternalApiResponse } from '../types/photo';
import { EXTERNAL_API_CONFIG, getApiUrl } from '../config/externalApi';

export class ExternalApiService {
  private static instance: ExternalApiService;
  
  private constructor() {}
  
  public static getInstance(): ExternalApiService {
    if (!ExternalApiService.instance) {
      ExternalApiService.instance = new ExternalApiService();
    }
    return ExternalApiService.instance;
  }

  /**
   * Send PDF data to webhook
   */
  public async sendPDFToExternalAPI(
    payload: ExternalApiPayload
  ): Promise<ExternalApiResponse> {
    try {
      const webhookUrl = getApiUrl(EXTERNAL_API_CONFIG.ENDPOINTS.UPLOAD_DOCUMENT);
      console.log('🌐 Webhook URL:', webhookUrl);
      console.log('🔐 Username:', EXTERNAL_API_CONFIG.WEBHOOK.USERNAME);
      console.log('📝 Payload size:', JSON.stringify(payload).length, 'bytes');
      
      const headers: Record<string, string> = {
        'Content-Type': EXTERNAL_API_CONFIG.WEBHOOK.CONTENT_TYPE,
      };

      // Add basic authentication using app-level credentials
      const authString = btoa(`${EXTERNAL_API_CONFIG.WEBHOOK.USERNAME}:${EXTERNAL_API_CONFIG.WEBHOOK.PASSWORD}`);
      headers['Authorization'] = `Basic ${authString}`;
      
      console.log('📋 Headers:', headers);
      console.log('📤 Sending POST request...');

      const response = await fetch(webhookUrl, {
        method: EXTERNAL_API_CONFIG.WEBHOOK.METHOD,
        headers,
        body: JSON.stringify(payload),
      });

      console.log('📥 Response status:', response.status);
      console.log('📥 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Webhook error response:', errorData);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: ExternalApiResponse = await response.json();
      console.log('✅ Webhook success response:', data);
      return data;

    } catch (error) {
      console.error('❌ Webhook error:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : 'Unknown'
      });
      throw new Error(
        error instanceof Error ? error.message : 'Failed to send document to webhook'
      );
    }
  }

  /**
   * Test connection to webhook
   */
  public async testConnection(): Promise<boolean> {
    try {
      const webhookUrl = getApiUrl(EXTERNAL_API_CONFIG.ENDPOINTS.UPLOAD_DOCUMENT);
      console.log('🧪 Testing webhook connection to:', webhookUrl);
      
      const authString = btoa(`${EXTERNAL_API_CONFIG.WEBHOOK.USERNAME}:${EXTERNAL_API_CONFIG.WEBHOOK.PASSWORD}`);
      console.log('🔐 Using credentials:', {
        username: EXTERNAL_API_CONFIG.WEBHOOK.USERNAME,
        passwordSet: !!EXTERNAL_API_CONFIG.WEBHOOK.PASSWORD
      });
      
      // Use the same endpoint as upload for testing (since it's a complete webhook URL)
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': EXTERNAL_API_CONFIG.WEBHOOK.CONTENT_TYPE,
          'Authorization': `Basic ${authString}`,
        },
        body: JSON.stringify({ test: true, timestamp: new Date().toISOString() }),
      });

      console.log('🧪 Test response status:', response.status);
      console.log('🧪 Test response ok:', response.ok);
      
      // Try to get response body for more details
      try {
        const responseText = await response.text();
        console.log('🧪 Test response body:', responseText);
      } catch (e) {
        console.log('🧪 Could not read response body');
      }
      
      return response.ok;
    } catch (error) {
      console.error('❌ Webhook connection test failed:', error);
      return false;
    }
  }

  /**
   * Test basic n8n server connectivity (without specific webhook)
   */
  public async testN8nServer(): Promise<boolean> {
    try {
      // Test the base n8n server
      const baseUrl = 'https://n8n.invoicefactory.eu';
      console.log('🧪 Testing n8n server connectivity to:', baseUrl);
      
      const response = await fetch(baseUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
        },
      });

      console.log('🧪 n8n server response status:', response.status);
      console.log('🧪 n8n server response ok:', response.ok);
      
      return response.ok;
    } catch (error) {
      console.error('❌ n8n server connectivity test failed:', error);
      return false;
    }
  }

  /**
   * Get API configuration
   */
  // Get API configuration
  public getApiConfig() {
    return {
      url: EXTERNAL_API_CONFIG.BASE_URL,
      endpoints: EXTERNAL_API_CONFIG.ENDPOINTS,
      limits: EXTERNAL_API_CONFIG.LIMITS,
    };
  }
}

export default ExternalApiService.getInstance();
